import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { imgUrl } from "@/lib/api";
import { ImageUpload } from "./ImageUpload";

const EMPTY = { name: "", city: "", quote: "", product: "", duration: "", before_image: "", after_image: "", active: true };
const input = "h-11 w-full rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gold)]";

export function TestimonialForm({ open, onOpenChange, initial, onSave, saving }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => {
    if (open) setF(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);
  const up = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const ready = f.name && f.quote && f.before_image && f.after_image;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="testimonial-form-dialog" data-lenis-prevent className="max-h-[90vh] max-w-2xl overflow-y-auto border-[var(--line)] bg-[var(--ink)] text-[var(--parchment)]">
        <DialogHeader><DialogTitle className="font-display text-3xl italic">{initial ? "Modifier le témoignage" : "Nouveau témoignage"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (ready) onSave(f); }} className="grid gap-4 sm:grid-cols-2">
          <ImageUpload testid="tf-before" label="Photo avant" value={f.before_image} onChange={(u) => setF((s) => ({ ...s, before_image: u }))} />
          <ImageUpload testid="tf-after" label="Photo après" value={f.after_image} onChange={(u) => setF((s) => ({ ...s, after_image: u }))} />
          <input data-testid="tf-name" required placeholder="Prénom de la cliente" value={f.name} onChange={up("name")} className={input} />
          <input data-testid="tf-city" placeholder="Ville" value={f.city} onChange={up("city")} className={input} />
          <input data-testid="tf-product" placeholder="Produit utilisé" value={f.product} onChange={up("product")} className={input} />
          <input data-testid="tf-duration" placeholder="Durée (ex. 3 mois)" value={f.duration} onChange={up("duration")} className={input} />
          <textarea data-testid="tf-quote" required rows={4} placeholder="Témoignage" value={f.quote} onChange={up("quote")} className={input + " h-auto py-2 sm:col-span-2"} />
          <label className="flex items-center gap-2 text-sm"><input data-testid="tf-active" type="checkbox" checked={f.active} onChange={up("active")} /> Visible sur le site</label>
          <button data-testid="tf-submit" disabled={saving || !ready} className="h-12 rounded-full bg-[var(--gold)] text-xs font-semibold uppercase tracking-[0.24em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-40 sm:col-span-2">
            {saving ? "Enregistrement…" : ready ? "Enregistrer" : "Ajoutez les 2 photos, un prénom et un texte"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function TestimonialsTable({ items, onEdit, onDelete }) {
  if (!items.length) return <p data-testid="admin-testimonials-empty" className="py-16 text-center font-display text-2xl italic text-[var(--muted)]">Aucun témoignage.</p>;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((t) => (
        <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-4">
          <div className="grid grid-cols-2 gap-2">
            <img src={imgUrl(t.before_image)} alt="avant" className="aspect-square w-full rounded-sm object-cover" />
            <img src={imgUrl(t.after_image)} alt="après" className="aspect-square w-full rounded-sm object-cover" />
          </div>
          <p className="mt-4 font-display text-lg">{t.name} {t.active ? "" : <span className="text-xs text-[var(--muted)]">(masqué)</span>}</p>
          <p className="mt-1 line-clamp-2 text-sm text-[var(--muted)]">{t.quote}</p>
          <div className="mt-4 flex justify-end gap-2">
            <button data-testid={`admin-testimonial-edit-${t.id}`} onClick={() => onEdit(t)} className="rounded-full border border-[var(--line)] p-2 hover:border-[var(--gold)]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => onDelete(t)} className="rounded-full border border-[var(--line)] p-2 hover:border-red-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>
      ))}
    </div>
  );
}
